import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.tereiko.com/api/v1';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10);

// Create Axios instance with cookie support
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true, // Enable sending/receiving cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add tenant ID header
// Note: Authentication is handled automatically via httpOnly cookies
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add tenant ID header if available
    const tenantId = localStorage.getItem('tereiko-active-tenant-id');
    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh state
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

function onRefreshComplete(success: boolean) {
  refreshSubscribers.forEach((cb) => cb(success));
  refreshSubscribers = [];
}

function waitForRefresh(): Promise<boolean> {
  return new Promise((resolve) => {
    refreshSubscribers.push(resolve);
  });
}

// Singleflight for explicit /auth/refresh callers (init flow, learner refresh,
// any code that calls refresh directly). With rotating refresh tokens, two
// concurrent calls race: one rotates the cookie, the other presents the now-
// revoked token and 401s. Funnel everyone through one in-flight promise.
export interface RefreshResponseBody {
  status?: string;
  message?: string;
  data?: { access_token?: string };
}

let activeRefresh: Promise<RefreshResponseBody> | null = null;

export function refreshAccessTokenSingleflight(): Promise<RefreshResponseBody> {
  if (activeRefresh) return activeRefresh;
  activeRefresh = api
    .post<RefreshResponseBody>('/auth/refresh')
    .then((res) => res.data)
    .finally(() => {
      activeRefresh = null;
    });
  return activeRefresh;
}

// Endpoints whose own 401 is meaningful (bad credentials, expired id_token, revoked
// token) and must reach the caller. Matching is substring-based, so '/auth/google/signup'
// needs its own entry — it does not contain '/auth/signup'.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/signup', '/auth/google/signup', '/auth/google', '/auth/refresh', '/auth/logout', '/auth/me', '/auth/forgot-password', '/auth/reset-password'];

// Response interceptor: Handle errors with automatic token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (error.response?.status === 401) {
      // Don't retry auth endpoints — reject immediately
      const url = originalRequest?.url || '';
      if (AUTH_ENDPOINTS.some((ep) => url.includes(ep)) || originalRequest?._retried) {
        return Promise.reject(error);
      }

      // If a refresh is already in progress, wait for it
      if (isRefreshing) {
        const success = await waitForRefresh();
        if (success) {
          originalRequest._retried = true;
          return api(originalRequest);
        }
        if (!window.location.pathname.startsWith('/auth')) {
          window.location.href = '/auth/login';
        }
        return Promise.reject(error);
      }

      // Attempt token refresh — use singleflight so explicit refresh callers
      // and this 401-retry path share the same in-flight promise.
      isRefreshing = true;
      try {
        await refreshAccessTokenSingleflight();
        isRefreshing = false;
        onRefreshComplete(true);
        // Retry the original request
        originalRequest._retried = true;
        return api(originalRequest);
      } catch {
        isRefreshing = false;
        onRefreshComplete(false);
        if (!window.location.pathname.startsWith('/auth')) {
          window.location.href = '/auth/login';
        }
        return Promise.reject(error);
      }
    }

    // Handle 400 Bad Request (validation errors)
    if (error.response?.status === 400) {
      console.error('Bad Request (400):', {
        url: error.config?.url,
        method: error.config?.method,
        data: error.response.data,
        message: (error.response.data as any)?.message || 'Invalid request'
      });
      // Don't logout user - let component handle error display
    }

    // Handle 403 Forbidden (insufficient permissions)
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      // Could trigger a toast notification here
    }

    // Handle 404 Not Found (resource doesn't exist)
    if (error.response?.status === 404) {
      console.error('Not Found (404):', {
        url: error.config?.url,
        method: error.config?.method,
        data: error.response.data,
        message: (error.response.data as any)?.message || 'Resource not found'
      });
      // Don't logout user - let component handle error display
    }

    // Handle 429 Too Many Requests (rate limiting)
    if (error.response?.status === 429) {
      console.warn('Rate limit exceeded. Please try again later.');
      // Could trigger a toast notification here
    }

    // Handle network errors
    if (!error.response) {
      console.error('Network error:', error.message);
      // Could trigger a toast notification here
    }

    return Promise.reject(error);
  }
);

export default api;

// Helper function to set active tenant
export const setActiveTenant = (tenantId: string): void => {
  localStorage.setItem('tereiko-active-tenant-id', tenantId);
};

// Helper function to get active tenant
export const getActiveTenant = (): string | null => {
  return localStorage.getItem('tereiko-active-tenant-id');
};

// Helper function to clear active tenant
export const clearActiveTenant = (): void => {
  localStorage.removeItem('tereiko-active-tenant-id');
};

// Typed API methods
export const apiClient = {
  get: <T = unknown>(url: string, config = {}) =>
    api.get<T>(url, config).then((res) => res.data),

  post: <T = unknown>(url: string, data?: unknown, config = {}) =>
    api.post<T>(url, data, config).then((res) => res.data),

  put: <T = unknown>(url: string, data?: unknown, config = {}) =>
    api.put<T>(url, data, config).then((res) => res.data),

  patch: <T = unknown>(url: string, data?: unknown, config = {}) =>
    api.patch<T>(url, data, config).then((res) => res.data),

  delete: <T = unknown>(url: string, config = {}) =>
    api.delete<T>(url, config).then((res) => res.data),
};
